import { useState, useCallback, useRef } from 'react';
import { auth } from '../firebase';
import type { PropertyData, BuyerCriteria, MatchScoreResult } from './matchingScoreEngine';
import {
  displayCity,
  displayCityArray,
  displayNeighborhood,
  displayNeighborhoodArray,
  displayPropertyType,
  displayPropertyTypeArray,
  displayCondition,
  displayConditionArray,
} from './displayLabels';

export type RecommendationLevel = 'present_now' | 'worth_presenting' | 'discuss_first' | 'weak_alternative';
export type NextStep = 'present_immediately' | 'present_after_clarifying' | 'keep_as_backup' | 'skip';

export interface AIRecommendation {
  recommendation_level: RecommendationLevel;
  strategic_opportunity: string;
  primary_selling_angle: string;
  opening_sentence: string;
  conversation_flow: string[];
  most_likely_objection: string;
  recommended_response: string;
  avoid_talking_about: string[];
  why_this_property_first: string;
  recommended_next_step: NextStep;
  final_recommendation: string;
}

interface RecommendationState {
  loading: boolean;
  error: string | null;
  recommendation: AIRecommendation | null;
}

export const AI_MATCH_RECOMMENDATION_LIMIT = 5;
export const AI_MATCH_MIN_SCORE = 75;

type CacheKey = string;

function makeCacheKey(buyerId: string, propertyId: string): CacheKey {
  return `${buyerId}::${propertyId}`;
}

export function useMatchRecommendation() {
  const [states, setStates] = useState<Record<CacheKey, RecommendationState>>({});
  const cache = useRef<Record<CacheKey, AIRecommendation>>({});
  const inFlight = useRef<Set<CacheKey>>(new Set());

  const getState = useCallback(
    (buyerId: string, propertyId: string): RecommendationState => {
      const key = makeCacheKey(buyerId, propertyId);
      return (
        states[key] || {
          loading: false,
          error: null,
          recommendation: cache.current[key] || null,
        }
      );
    },
    [states],
  );

  const generate = useCallback(
    async (
      buyerId: string,
      propertyId: string,
      buyer: BuyerCriteria,
      property: PropertyData,
      match: MatchScoreResult,
      rankContext?: { rankPosition: number; totalQualityMatches: number; matchScope?: 'agent' | 'office' },
    ): Promise<void> => {
      const key = makeCacheKey(buyerId, propertyId);

      // Return cached result immediately
      if (cache.current[key]) {
        setStates(prev => ({
          ...prev,
          [key]: { loading: false, error: null, recommendation: cache.current[key] },
        }));
        return;
      }

      // Prevent duplicate calls
      if (inFlight.current.has(key)) return;
      inFlight.current.add(key);

      setStates(prev => ({
        ...prev,
        [key]: { loading: true, error: null, recommendation: null },
      }));

      try {
        if (!auth.currentUser) {
          throw new Error('Not authenticated');
        }

        const idToken = await auth.currentUser.getIdToken();
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

        const response = await fetch(
          `${supabaseUrl}/functions/v1/generate-match-recommendation`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${idToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              buyer: {
                cities: displayCityArray(buyer.cities),
                neighborhoods: displayNeighborhoodArray(buyer.neighborhoods),
                priceMin: buyer.priceMin,
                priceMax: buyer.priceMax,
                rooms: buyer.rooms,
                propertyTypes: displayPropertyTypeArray(buyer.propertyTypes),
                minSize: buyer.minSize,
                maxSize: buyer.maxSize,
                parkingRequired: buyer.parkingRequired,
                elevatorRequired: buyer.elevatorRequired,
                saferoomRequired: buyer.saferoomRequired,
                balconyRequired: buyer.balconyRequired,
                conditionPreferences: displayConditionArray(buyer.conditionPreferences),
              },
              property: {
                city: displayCity(property.city),
                neighborhood: displayNeighborhood(property.neighborhood),
                price: property.price,
                rooms: property.rooms,
                propertyType: displayPropertyType(property.propertyType),
                size: property.size,
                hasParking: property.hasParking,
                hasElevator: property.hasElevator,
                hasSaferoom: property.hasSaferoom,
                hasBalcony: property.hasBalcony,
                propertyCondition: displayCondition(property.propertyCondition),
                daysOnMarket: property.daysOnMarket,
                priceReducedRecently: property.priceReducedRecently,
                isFreshListing: property.isFreshListing,
              },
              match: {
                score: match.score,
                level: match.level,
                breakdown: match.breakdown,
                matchedFields: match.matchedFields,
                partialFields: match.partialFields,
                mismatchFields: match.mismatchFields,
              },
              rank: rankContext
                ? {
                    rankPosition: rankContext.rankPosition,
                    totalQualityMatches: rankContext.totalQualityMatches,
                    matchScope: rankContext.matchScope ?? 'agent',
                  }
                : undefined,
            }),
          },
        );

        if (!response.ok) {
          throw new Error('AI request failed');
        }

        const data = await response.json();
        const recommendation = data.recommendation as AIRecommendation;

        cache.current[key] = recommendation;
        setStates(prev => ({
          ...prev,
          [key]: { loading: false, error: null, recommendation },
        }));
      } catch (err) {
        setStates(prev => ({
          ...prev,
          [key]: {
            loading: false,
            error: err instanceof Error ? err.message : 'Unknown error',
            recommendation: null,
          },
        }));
      } finally {
        inFlight.current.delete(key);
      }
    },
    [],
  );

  return { getState, generate };
}
